/**
 * OnboardingGuard
 *
 * Redirects the owner of the active organization to `/setup` until they have
 * completed the YAML onboarding flow (preferences.onboardingCompleted === true).
 * Invited members (any other role) are never sent there.
 *
 * Mounted INSIDE ConsentGuard so it only runs after consent has been accepted
 * and the user actually has access to protected content. The /setup route
 * itself (registered by frontend-plugin-onboarding-yaml) is NOT wrapped in
 * ConsentGuard, which is what breaks the redirect loop.
 */

import { LoadingSpinner, useAuth, usePreferences } from '@xhubio-saas/core-frontend/kernel'
import { useEffect, useReducer } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { trpc } from '@/trpc'
import { WelcomeBanner } from './WelcomeBanner'

interface OnboardingGuardProps {
  children: React.ReactNode
}

/**
 * Parity fix (invoice-api, 2026-08-06): the kernel's `preferences.get` query
 * is neither user-scoped nor invalidated on auth change — after a client-side
 * sign-out → sign-in/register (same QueryClient), it serves the PREVIOUS
 * account's preferences for up to its staleTime. A brand-new user then skipped
 * /setup entirely (previous account had onboardingCompleted=true) and a
 * completed user got redirected to /setup again. The guard therefore redirects
 * only once preferences have been re-fetched for the CURRENT user id.
 *
 * The same holds for `organization.current` (one query key for every user):
 * its `userRole` decides the gate, so it is re-fetched together with the
 * preferences (parity: saas-invoice-api-frontend `be0134f`).
 *
 * Module-scoped so page navigations (the guard mounts per page) don't refetch
 * again for the same user.
 */
let verifiedForUser: string | null = null

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { session } = useAuth()
  const userId = session?.user?.id ?? null
  const { preferences, isLoading, refetch } = usePreferences()
  // Same options as `useIsOrgAdminRole` (hooks/useMemberAdminRights) — one cache entry.
  const org = trpc.organization.current.useQuery(undefined, { staleTime: 5 * 60 * 1000 })
  const refetchOrg = org.refetch
  const { pathname } = useLocation()
  const [, rerender] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    if (!userId || verifiedForUser === userId) return
    void Promise.all([refetch(), refetchOrg()]).then(() => {
      verifiedForUser = userId
      rerender()
    })
  }, [userId, refetch, refetchOrg])

  const freshForThisUser = userId !== null && verifiedForUser === userId

  // While the role is still loading: spinner, no flash of the page and no redirect (ENTSCHEID observer 8081).
  if (isLoading || org.isLoading || (userId !== null && !freshForThisUser)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // The survey asks about the company and `submit` files it under the active
  // organization — after accepting an invitation that is the inviter's. Only
  // the owner of the active organization is sent there (Issue PDF-API
  // 2026-10-15, KW-005). Exactly 'owner', lower case — the string core-backend
  // writes for the creator. No active organization (NOT_FOUND) ⇒ no redirect.
  const ownsActiveOrg = org.data?.userRole === 'owner'
  if (ownsActiveOrg && !preferences.onboardingCompleted && pathname !== '/setup') {
    return <Navigate to="/setup" replace />
  }

  // Suppress the welcome banner on setup-related routes. /setup hosts the
  // YAML flow itself, /setup/thanks is the closing screen which already
  // surfaces the same quick-links — both would clash with the banner.
  const showWelcomeBanner = !pathname.startsWith('/setup')

  return (
    <>
      {showWelcomeBanner ? <WelcomeBanner /> : null}
      {children}
    </>
  )
}
